import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CollectionsIcon from '@mui/icons-material/Collections';
import DeleteIcon from '@mui/icons-material/Delete';
import { ImageGalleryPicker } from './ImageGalleryPicker';

export const ImageSourceSelector = ({ imageUrl, onChange, onError, title = 'Imagem' }) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(imageUrl || null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      onError?.('Por favor, selecione um arquivo de imagem');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      onError?.('Arquivo muito grande. Máximo 5MB');
      return;
    }

    // Preview local
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result);
    reader.readAsDataURL(file);

    try {
      setLoading(true);

      const formData = new FormData();
      formData.append('file', file);

      const token = localStorage.getItem('agendario_jwt_token');
      if (!token) throw new Error('Token de autenticação não encontrado. Faça login novamente.');

      const uploadApiUrl = import.meta.env.VITE_UPLOAD_API_URL;
      if (!uploadApiUrl) throw new Error('URL da API de upload não configurada');

      const response = await fetch(`${uploadApiUrl}/api/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Erro no upload: ${response.status}`);
      }

      const data = await response.json();
      if (!data.fileUrl) throw new Error('URL do arquivo não retornada');

      onChange?.(data.fileUrl);
      setPreview(data.fileUrl);
    } catch (error) {
      console.error('[ImageSourceSelector] Upload falhou:', error);
      setPreview(imageUrl || null);
      onError?.(error.message || 'Erro ao fazer upload da imagem');
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleGallerySelect = (fullCdnUrl) => {
    onChange?.(fullCdnUrl);
    setPreview(fullCdnUrl);
  };

  const handleRemove = () => {
    onChange?.(null);
    setPreview(null);
  };

  return (
    <Box>
      {/* Preview */}
      {preview && (
        <Box sx={{ position: 'relative', mb: 1, maxWidth: 400 }}>
          <Box
            component="img"
            src={preview}
            alt={title}
            sx={{
              width: '100%',
              maxHeight: 180,
              objectFit: 'cover',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
          <Tooltip title="Remover imagem">
            <IconButton
              onClick={handleRemove}
              size="small"
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
        >
          Enviar Imagem
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<CollectionsIcon />}
          onClick={() => setGalleryOpen(true)}
          disabled={loading}
        >
          Escolher da Galeria
        </Button>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={handleFileSelect}
        disabled={loading}
      />

      <ImageGalleryPicker
        open={galleryOpen}
        onClose={() => setGalleryOpen(false)}
        onSelect={handleGallerySelect}
        currentImageUrl={imageUrl}
        title={title}
      />
    </Box>
  );
};
